import { FieldValues } from "react-hook-form";

import {
  controlNameMetadataResolve,
  FEAvailableSystemParamMetadata,
  FIELD_TYPE_IDS,
  fieldNameMetadataResolve,
  FORM_FIELD_VALUES,
  getIsShownByConditionRenderTemplate,
  ICustomField,
  IExtendableDto,
  PermissionMap,
  RydeUiUnit,
} from "@ryde/shared";

const BULK_FORM_GROUP_HEADER_FIELD_TYPE_IDS = new Set<FORM_FIELD_VALUES>([
  FIELD_TYPE_IDS.FORM.group_title,
  FIELD_TYPE_IDS.FORM.section_title,
]);

const BULK_NON_EDITABLE_FIELD_TYPE_IDS = new Set<FORM_FIELD_VALUES>([
  FIELD_TYPE_IDS.FORM.hidden_field,
]);

const METADATA_REMARKS_FIELD_NAME = "metadata.remarks";

const MULTISELECT_BULK_ACTION_FIELD_TYPE_IDS = new Set<FORM_FIELD_VALUES>([
  FIELD_TYPE_IDS.FORM.multiselect,
  FIELD_TYPE_IDS.FORM.multiselect_bound,
  FIELD_TYPE_IDS.FORM.tags,
]);

export const isMultiselectBulkFieldType = (
  fieldTypeId: ICustomField["formFieldTypeId"],
): boolean => {
  if (!fieldTypeId) {
    return false;
  }

  return MULTISELECT_BULK_ACTION_FIELD_TYPE_IDS.has(
    Number(fieldTypeId) as FORM_FIELD_VALUES,
  );
};

export const isBulkFormGroupHeaderControl = (
  control: ICustomField,
): boolean => {
  if (!control.formFieldTypeId) {
    return false;
  }

  return BULK_FORM_GROUP_HEADER_FIELD_TYPE_IDS.has(control.formFieldTypeId);
};

export const doesControlRequireBulkAction = ({
  control,
}: {
  control: ICustomField;
  entity: RydeUiUnit;
}): boolean => {
  // ADD/REMOVE/SET instead of replacing the whole array on every selected row
  if (isMultiselectBulkFieldType(control.formFieldTypeId)) {
    return true;
  }

  if (controlNameMetadataResolve(control) !== METADATA_REMARKS_FIELD_NAME) {
    return false;
  }

  return Number(control.formFieldTypeId) === FIELD_TYPE_IDS.FORM.textarea;
};

export const getShouldHideBulkControlByAdminMode = ({
  fieldNameResolved,
  isAdminModeEntity,
  shouldShowClientPrice,
  shouldShowSupplierPrice,
}: {
  fieldNameResolved: string;
  isAdminModeEntity: boolean;
  shouldShowClientPrice: boolean;
  shouldShowSupplierPrice: boolean;
}): boolean => {
  if (!isAdminModeEntity) {
    return false;
  }

  if (fieldNameResolved === "metadata.manualPrice" && !shouldShowClientPrice) {
    return true;
  }

  if (
    (fieldNameResolved === "metadata.supplierManualPrice" ||
      fieldNameResolved === "supplierPrice") &&
    !shouldShowSupplierPrice
  ) {
    return true;
  }

  return false;
};

export const isBulkControlRowVisible = ({
  control,
  formValues,
  dto,
  permissions,
  systemParams,
  isFieldVisible,
  isAdminModeEntity,
  shouldShowClientPrice,
  shouldShowSupplierPrice,
  isEdit,
}: {
  control: ICustomField;
  formValues: FieldValues;
  dto?: IExtendableDto;
  permissions?: PermissionMap;
  systemParams?: FEAvailableSystemParamMetadata;
  isFieldVisible: boolean;
  isAdminModeEntity: boolean;
  shouldShowClientPrice: boolean;
  shouldShowSupplierPrice: boolean;
  isEdit: boolean;
}): boolean => {
  const fieldTypeId = control.formFieldTypeId
    ? (Number(control.formFieldTypeId) as FORM_FIELD_VALUES)
    : null;

  if (fieldTypeId && BULK_NON_EDITABLE_FIELD_TYPE_IDS.has(fieldTypeId)) {
    return false;
  }

  const isShownByCondition = getIsShownByConditionRenderTemplate({
    formValues,
    formVisibleCondition: control.formVisibleCondition,
    isCheckDtoValuesForVisibleConditions:
      control.formAttributes?.checkDtoValuesForVisibleConditions ?? false,
    dto,
    isCheckSystemParamValuesForVisibleConditions:
      control.formAttributes?.checkSystemParamValuesForVisibleConditions ??
      false,
    systemParams,
    permissions,
  });

  if (!isShownByCondition) {
    return false;
  }

  if (!isFieldVisible) {
    return false;
  }

  const fieldNameResolved = fieldNameMetadataResolve(
    control.name,
    control.isColumn,
  );

  if (
    getShouldHideBulkControlByAdminMode({
      fieldNameResolved,
      isAdminModeEntity,
      shouldShowClientPrice,
      shouldShowSupplierPrice,
    })
  ) {
    return false;
  }

  if (isEdit && control.formAttributes?.isAddOnly) {
    return false;
  }

  return true;
};
